import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ORDER_API } from "../../data/api";

const SellerHome = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [bestProducts, setBestProducts] = useState([]);

  const pieColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const vendorId = localStorage.getItem("vendorId");
        const response = await axios.get(`${ORDER_API.GET_ALL}/vendor/${vendorId}`);
        const ordersData = response.data;
        setOrders(ordersData);
        const months = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const monthlySales = months.map((month, index) => {
          const total = ordersData
            .filter(order => new Date(order.createdAt).getMonth() === index)
            .reduce((sum, order) => sum + parseFloat(order.finalAmount), 0);
          return { month, sales: total };
        });
        setSalesData(monthlySales);
        const categoryCounts = {};
        ordersData.forEach(order => {
          order.items.forEach(item => {
            const category = item.product.categoryName || "Others";
            categoryCounts[category] = (categoryCounts[category] || 0) + 1;
          });
        });
        const categoryArray = Object.keys(categoryCounts).map(key => ({
          name: key,
          value: categoryCounts[key],
        }));
        setCategoryData(categoryArray);

        // Best selling products
        const productCounts = {};
        ordersData.forEach(order => {
          order.items.forEach(item => {
            const name = item.product.productName;
            productCounts[name] = (productCounts[name] || 0) + item.quantity;
          });
        });
        const bestProductsArray = Object.keys(productCounts)
          .map(name => ({ name, sales: productCounts[name] }))
          .sort((a, b) => b.sales - a.sales)
          .slice(0, 5);
        setBestProducts(bestProductsArray);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Calculating total orders, pending orders, and total sales
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.orderStatus === "Pending").length;
  const totalSales = orders.reduce((sum, o) => sum + parseFloat(o.finalAmount), 0);

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <h1 className="text-2xl font-bold text-gray-800">Vendor Dashboard</h1>

      {/* SMALL TOP CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-4 shadow-sm border rounded-xl text-center">
          <p className="text-gray-500 text-sm">Total Orders</p>
          <h2 className="text-2xl font-bold text-blue-600">{totalOrders}</h2>
        </div>

        <div className="bg-white p-4 shadow-sm border rounded-xl text-center">
          <p className="text-gray-500 text-sm">Pending Orders</p>
          <h2 className="text-2xl font-bold text-yellow-600">{pendingOrders}</h2>
        </div>

        <div className="bg-white p-4 shadow-sm border rounded-xl text-center">
          <p className="text-gray-500 text-sm">Total Sales</p>
          <h2 className="text-2xl font-bold text-green-600">₹{totalSales}</h2>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* SALES BAR CHART */}
        <div className="bg-white p-4 shadow-sm border rounded-xl lg:col-span-2">
          <h2 className="text-lg font-semibold mb-3">Monthly Sales</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={salesData}>
              <XAxis dataKey="month" />
              <Tooltip />
              <Bar dataKey="sales" barSize={28} fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* PIE CHART */}
        <div className="bg-white p-4 shadow-sm border rounded-xl">
          <h2 className="text-lg font-semibold mb-3">Category Share</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={categoryData} dataKey="value" nameKey="name" outerRadius={70} label>
                {categoryData.map((_, i) => (
                  <Cell key={i} fill={pieColors[i % pieColors.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* TABLE SECTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* RECENT ORDERS */}
        <div className="bg-white p-4 shadow-sm border rounded-xl">
          <h2 className="text-lg font-semibold mb-3">Recent Orders</h2>
          <ul className="space-y-3">
            {orders.slice(0, 5).map(order => (
              <li key={order.orderId} className="border p-3 rounded-lg">
                <p className="font-medium text-sm">
                  {order.items.map(i => i.product.productName).join(", ")}
                </p>
                <div className="flex justify-between items-center text-xs mt-1">
                  <span className="text-gray-600">₹{order.finalAmount}</span>
                  <span
                    className={`px-2 py-1 rounded text-[10px] font-medium ${
                      order.orderStatus === "Delivered"
                        ? "bg-green-100 text-green-700"
                        : order.orderStatus === "Pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {order.orderStatus}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* BEST SELLING PRODUCTS */}
        <div className="bg-white p-4 shadow-sm border rounded-xl">
          <h2 className="text-lg font-semibold mb-3">Top Selling Products</h2>
          <ul className="space-y-3">
            {bestProducts.map((p, i) => (
              <li key={i} className="flex justify-between items-center border p-3 rounded-lg">
                <span className="text-gray-800">{p.name}</span>
                <span className="text-blue-600 font-semibold text-sm">{p.sales} sold</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SellerHome;
